import { Matrix4 } from 'three';
import type { Bone, Object3D } from 'three';

/** A bone of a skinned body, and the rig joint that moves it. */
export interface BonePair {
  readonly bone: Bone;
  readonly driver: Object3D;
}

const wanted = new Matrix4();
const parentInverse = new Matrix4();

/**
 * Lets a skinned body made in Blender follow the game's rig: each bone keeps, relative to its joint, the place it had
 * when both stood at rest, however the animation moves the joints. The rig stays the one thing the animation drives.
 * Pairs go parents first; both hierarchies must be at rest, with their world matrices up to date, when it is made.
 */
export class SkinFollower {
  private readonly offsets: Matrix4[];
  private readonly worlds = new Map<Object3D, Matrix4>();

  constructor(private readonly pairs: readonly BonePair[]) {
    this.offsets = pairs.map(({ bone, driver }) => driver.matrixWorld.clone().invert().multiply(bone.matrixWorld));
    for (const { bone } of pairs) this.worlds.set(bone, new Matrix4());
  }

  /** After the rig has moved and its world matrices are up to date. */
  update(): void {
    this.pairs.forEach(({ bone, driver }, i) => {
      wanted.multiplyMatrices(driver.matrixWorld, this.offsets[i]);
      this.worlds.get(bone)!.copy(wanted);
      // A bone's parent is either a bone placed just before it, or something that does not move with the rig.
      const parent = bone.parent;
      const parentWorld = parent ? (this.worlds.get(parent) ?? parent.matrixWorld) : null;
      if (parentWorld) wanted.premultiply(parentInverse.copy(parentWorld).invert());
      wanted.decompose(bone.position, bone.quaternion, bone.scale);
    });
  }
}
